import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  ScheduleTaskType,
  parseDateOnly,
  scheduleNonWorkDates,
  scheduleTaskCalendarDays,
  scheduleTaskNonWorkOverlap,
  scheduleTaskWorkdays,
  type ScheduleSourceRef,
} from '@construction/shared';
import { Badge } from '@/components/ui';
import { cn } from '@/lib/cn';
import { DAY_MS, dayList, ganttRange, monthSegments, weekStarts } from './gantt-layout';

/**
 * 甘特图（CR-080，CR-081 按方向稿重做外观）。网页端和 App 同一个组件，按 `columns` 换密度。
 *
 * **左表和时间轴是并排的两块，只有时间轴横着滚**：不用 sticky 列，也就不用往层级里塞数字。两边行高一样，竖着一起走。
 *
 * **两块之间的分隔线能拖**（客户要的：「让左侧整个区域可以往左边拖动，这样右侧 chart 就能完全显示」）：
 * 左表最宽是它本来的宽度，最窄可以拖到 0；让出来的宽度给时间轴 —— 按周看时时间轴本来就铺满，拖窄左表整张图就放得下。
 * 左表被拖窄时它自己能横着滚，藏起来的列不会够不着。鼠标、手指、键盘（←/→，Home 收起、End 展开）都能拖，
 * 双击分隔线在「收起 / 展开」之间切。宽度按 `columns` 分别记在本机（`useSplitWidth`），换一种左表各记各的。
 *
 * **该看的都写在格子里**：手机上没有悬停（CR-021 图钉那一课），手机上（`compact`）名称下面第二行就是起止和工作日。
 * 电脑上鼠标移到一行，两边一起高亮；移到横条上浮出一张详情卡 —— 这是**加分项**，卡片里的每一项表里都有，
 * 只在真的有鼠标（`hover: hover`）时出现。
 *
 * 颜色全走 token；「原表颜色」用的是数据里的颜色（从 PDF 读出来的），不是写在代码里的色值。
 */

export type GanttScale = 'week' | 'day';
export type GanttColorMode = 'type' | 'source';
export type GanttColumns = 'full' | 'lite' | 'compact';

export interface GanttTask {
  key: string;
  code?: string | null;
  name: string;
  type: ScheduleTaskType;
  plannedStart: string;
  plannedFinish: string;
  color?: string | null;
  sourceRef?: ScheduleSourceRef[] | null;
}

type ColumnKey = 'code' | 'name' | 'type' | 'start' | 'finish' | 'workdays' | 'calendarDays' | 'source';

const COLUMN_WIDTH: Record<ColumnKey, number> = {
  code: 52,
  name: 232,
  type: 84,
  start: 136,
  finish: 136,
  workdays: 72,
  calendarDays: 72,
  source: 150,
};

/** 精简：名称 + 短日期（「9/07 周一」），把宽度让给时间轴 —— 1100px 的窗口里完整日期会把时间轴挤到一百多像素 */
const LITE_DATE_WIDTH = 104;

const COLUMNS: Record<Exclude<GanttColumns, 'compact'>, ColumnKey[]> = {
  lite: ['name', 'start', 'finish'],
  full: ['code', 'name', 'type', 'start', 'finish', 'workdays', 'calendarDays', 'source'],
};

const RIGHT_ALIGNED: ReadonlySet<ColumnKey> = new Set(['workdays', 'calendarDays']);

/** 手机上左边只留一列：名称 + 第二行的起止 */
const COMPACT_WIDTH = 148;

const BAR_CLASS: Record<ScheduleTaskType, string> = {
  WORK: 'bg-chart-work',
  INSPECTION: 'bg-chart-inspection',
  NONWORK: 'border border-ink-3 bg-[repeating-linear-gradient(135deg,var(--ink-3)_0_2px,transparent_2px_5px)]',
};

const TYPE_TONE: Record<ScheduleTaskType, 'brand' | 'warn' | 'neutral'> = {
  WORK: 'brand',
  INSPECTION: 'warn',
  NONWORK: 'neutral',
};

interface Tip {
  task: GanttTask;
  x: number;
  y: number;
}

export function GanttChart({
  tasks,
  workdays,
  today,
  scale,
  colorMode,
  columns,
}: {
  tasks: readonly GanttTask[];
  workdays: readonly number[];
  /** 工地时区里的今天（`YYYY-MM-DD`） */
  today: string;
  scale: GanttScale;
  colorMode: GanttColorMode;
  columns: GanttColumns;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const valid = useMemo(
    () =>
      tasks.filter((task) => {
        const s = parseDateOnly(task.plannedStart);
        const f = parseDateOnly(task.plannedFinish);
        return s !== null && f !== null && f >= s;
      }),
    [tasks],
  );
  const range = useMemo(() => ganttRange(valid), [valid]);
  const nonWork = useMemo(() => new Set(scheduleNonWorkDates(valid, workdays)), [valid, workdays]);

  const paneRef = useRef<HTMLDivElement>(null);
  const paneWidth = useElementWidth(paneRef);
  const [hovered, setHovered] = useState<string | null>(null);
  const [tip, setTip] = useState<Tip | null>(null);
  const canHover = useCanHover();

  const compact = columns === 'compact';
  const rowClass = compact ? 'h-13' : 'h-8';

  const cols = compact ? [] : COLUMNS[columns];
  const shortDates = columns === 'lite';
  const colWidth = (c: ColumnKey) => (shortDates && (c === 'start' || c === 'finish') ? LITE_DATE_WIDTH : COLUMN_WIDTH[c]);
  const leftWidth = compact ? COMPACT_WIDTH : cols.reduce((n, c) => n + colWidth(c), 0);
  const [leftSize, setLeftSize] = useSplitWidth(columns, leftWidth);
  const drag = useRef<{ x: number; from: number } | null>(null);
  const dw = !range ? 0 : scale === 'day' ? (compact ? 16 : 24) : Math.max(compact ? 3 : 4, paneWidth / range.days);
  const todayMs = parseDateOnly(today);

  // 打开、换刻度时把今天滚进视野（左边留几天，看得到前面刚做完的）
  useEffect(() => {
    const pane = paneRef.current;
    if (!pane || !range || todayMs === null || dw === 0) return;
    const at = ((todayMs - range.start) / DAY_MS - (compact ? 3 : 7)) * dw;
    pane.scrollLeft = Math.max(0, at);
  }, [range, todayMs, dw, scale, compact]);

  const fmt = useMemo(
    () => ({
      weekday: new Intl.DateTimeFormat(lang, { weekday: 'short', timeZone: 'UTC' }),
      narrowWeekday: new Intl.DateTimeFormat(lang, { weekday: 'narrow', timeZone: 'UTC' }),
      monthDay: new Intl.DateTimeFormat(lang, { month: 'numeric', day: 'numeric', timeZone: 'UTC' }),
      month: new Intl.DateTimeFormat(lang, { year: 'numeric', month: 'short', timeZone: 'UTC' }),
      monthShort: new Intl.DateTimeFormat(lang, { month: 'short', timeZone: 'UTC' }),
    }),
    [lang],
  );

  if (!range) return null;

  const width = range.days * dw;
  const x = (ms: number) => ((ms - range.start) / DAY_MS) * dw;
  const days = dayList(range);
  const isRest = (ms: number) => !workdays.includes(new Date(ms).getUTCDay());
  const dayKey = (ms: number) => new Date(ms).toISOString().slice(0, 10);
  const md = (value: string) => fmt.monthDay.format(parseDateOnly(value) ?? 0);
  const span = (task: Pick<GanttTask, 'plannedStart' | 'plannedFinish'>) =>
    task.plannedStart === task.plannedFinish ? md(task.plannedStart) : `${md(task.plannedStart)} – ${md(task.plannedFinish)}`;
  const showToday = todayMs !== null && todayMs >= range.start && todayMs <= range.end;

  const starts = valid.map((task) => parseDateOnly(task.plannedStart)!);
  const finishes = valid.map((task) => parseDateOnly(task.plannedFinish)!);
  const projectStart = Math.min(...starts);
  const projectFinish = Math.max(...finishes);
  // 「项目整体」那一行按一项施工来算工作日：开工到完工，扣周末和停工日
  const projectTask: GanttTask = {
    key: '__project',
    name: '',
    type: ScheduleTaskType.WORK,
    plannedStart: dayKey(projectStart),
    plannedFinish: dayKey(projectFinish),
  };

  // 表头：电脑上 月 / 周（/ 日）；手机上 月 / 日（按天）或 月 / 周（按周）
  const headRows: Array<'month' | 'week' | 'day'> = compact
    ? scale === 'day'
      ? ['month', 'day']
      : ['month', 'week']
    : scale === 'day'
      ? ['month', 'week', 'day']
      : ['month', 'week'];
  const headClass = { month: 'h-8', week: 'h-7', day: 'h-9' } as const;

  const workdaysOf = (task: GanttTask) => scheduleTaskWorkdays(task, workdays, nonWork);

  const cell = (task: GanttTask, col: ColumnKey): ReactNode => {
    switch (col) {
      case 'code':
        return <span className="font-mono text-caption text-ink-4">{task.code ?? ''}</span>;
      case 'name':
        return <span className="truncate font-medium text-ink">{task.name}</span>;
      case 'type':
        return <Badge tone={TYPE_TONE[task.type]}>{t(`schedule.type.${task.type}`)}</Badge>;
      case 'start':
      case 'finish': {
        const value = col === 'start' ? task.plannedStart : task.plannedFinish;
        return (
          <span className="whitespace-nowrap tabular-nums text-ink-2">
            {shortDates ? md(value) : value}
            <span className="ml-1.5 text-caption text-ink-4">{fmt.weekday.format(parseDateOnly(value)!)}</span>
          </span>
        );
      }
      case 'workdays': {
        const n = workdaysOf(task);
        const off = scheduleTaskNonWorkOverlap(task, nonWork);
        return n === null ? (
          <span className="text-ink-4">—</span>
        ) : (
          <span className="tabular-nums text-ink-2">
            {n}
            {off > 0 && <span className="ml-0.5 text-warn">*</span>}
          </span>
        );
      }
      case 'calendarDays':
        return <span className="tabular-nums text-ink-2">{scheduleTaskCalendarDays(task)}</span>;
      case 'source':
        return (
          <span className="truncate text-caption text-ink-3">
            {(task.sourceRef ?? []).map((r) => t('schedule.gantt.sourceRef', { page: r.page, row: r.weekRow })).join(' · ')}
          </span>
        );
    }
  };

  const rowHover = (key: string) =>
    canHover ? { onMouseEnter: () => setHovered(key), onMouseLeave: () => setHovered(null) } : {};
  const rowTone = (key: string) => (hovered === key ? 'bg-brand-50/70' : '');

  return (
    // clip 不是 hidden：hidden 会让这一块自己成为滚动容器，分隔线上的把手就 sticky 不到屏幕中间
    <div className="relative flex overflow-clip rounded-xl border border-rule bg-surface text-body shadow-xs">
      {/* ---------------- 左表：外面这层的宽度跟着分隔线走，里面按本来的宽度排，拖窄了就在这层里横着滚 */}
      <div className="shrink-0 overflow-x-auto overflow-y-hidden border-r border-rule" style={{ width: leftSize }}>
        <div style={{ width: leftWidth }}>
          {headRows.map((row, i) => (
            <div
              key={row}
              className={cn(headClass[row], 'flex border-b border-rule', i === headRows.length - 1 ? 'items-center' : '')}
            >
              {i === headRows.length - 1 &&
                (compact ? (
                  <HeadCell width={COMPACT_WIDTH}>{t('schedule.gantt.col.name')}</HeadCell>
                ) : (
                  cols.map((c) => (
                    <HeadCell key={c} width={colWidth(c)} right={RIGHT_ALIGNED.has(c)}>
                      {t(`schedule.gantt.col.${c}`)}
                    </HeadCell>
                  ))
                ))}
            </div>
          ))}

          {/* 项目整体 */}
          <div className={cn(rowClass, 'flex items-center border-b border-rule bg-surface-2/60')}>
            {compact ? (
              <div className="min-w-0 px-3">
                <div className="truncate font-semibold text-ink">{t('schedule.gantt.project')}</div>
                <div className="truncate text-caption tabular-nums text-ink-3">{span(projectTask)}</div>
              </div>
            ) : (
              cols.map((c) => (
                <BodyCell key={c} width={colWidth(c)} right={RIGHT_ALIGNED.has(c)}>
                  {c === 'name' ? (
                    <span className="font-semibold text-ink">{t('schedule.gantt.project')}</span>
                  ) : c === 'start' || c === 'finish' || c === 'workdays' || c === 'calendarDays' ? (
                    cell(projectTask, c)
                  ) : null}
                </BodyCell>
              ))
            )}
          </div>

          {valid.map((task) => (
            <div
              key={task.key}
              {...rowHover(task.key)}
              className={cn(rowClass, 'flex items-center border-b border-rule transition-colors last:border-0', rowTone(task.key))}
            >
              {compact ? (
                <div className="min-w-0 px-3">
                  <div className="truncate font-medium text-ink">{task.name}</div>
                  <div className="truncate text-caption tabular-nums text-ink-3">
                    {span(task)}
                    {task.type !== ScheduleTaskType.NONWORK &&
                      ` · ${t('schedule.gantt.workdays', { count: workdaysOf(task) ?? 0 })}`}
                  </div>
                </div>
              ) : (
                cols.map((c) => (
                  <BodyCell key={c} width={colWidth(c)} right={RIGHT_ALIGNED.has(c)}>
                    {cell(task, c)}
                  </BodyCell>
                ))
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ---------------- 时间轴：画给眼睛看的，内容左表里都有 */}
      <div ref={paneRef} aria-hidden className="min-w-0 flex-1 overflow-x-auto">
        <div className="relative" style={{ width }}>
          {headRows.map((row, i) => (
            <div key={row} className={cn(headClass[row], 'relative border-b border-rule')}>
              {row === 'month' &&
                monthSegments(range).map((m) => {
                  const w = m.days * dw;
                  return (
                    <div
                      key={m.start}
                      className="absolute inset-y-0 flex items-center overflow-hidden whitespace-nowrap border-l border-rule px-2 text-caption font-semibold text-ink first:border-l-0"
                      style={{ left: x(m.start), width: w }}
                    >
                      {w >= 76 ? fmt.month.format(m.start) : w >= 34 ? fmt.monthShort.format(m.start) : ''}
                    </div>
                  );
                })}
              {row === 'week' &&
                weekStarts(range).map((w) => (
                  <div
                    key={w.start}
                    className="absolute inset-y-0 flex items-center overflow-hidden whitespace-nowrap border-l border-rule/70 px-1.5 text-micro tabular-nums text-ink-3"
                    style={{ left: x(w.start), width: 7 * dw }}
                  >
                    {7 * dw >= 64 ? `W${w.index} · ${fmt.monthDay.format(w.start)}` : 7 * dw >= 30 ? fmt.monthDay.format(w.start) : ''}
                  </div>
                ))}
              {row === 'day' &&
                days.map((d) => (
                  <div
                    key={d}
                    className={cn(
                      'absolute inset-y-0 flex flex-col items-center justify-center text-micro leading-tight text-ink-4',
                      isRest(d) && 'bg-surface-2/70',
                      nonWork.has(dayKey(d)) && 'text-warn',
                    )}
                    style={{ left: x(d), width: dw }}
                  >
                    <span className="font-medium tabular-nums text-ink-2">{new Date(d).getUTCDate()}</span>
                    <span>{fmt.narrowWeekday.format(d)}</span>
                  </div>
                ))}
              {i === 0 && showToday && (
                <div
                  className="absolute top-1.5 flex h-5 -translate-x-1/2 items-center whitespace-nowrap rounded-full bg-danger-solid px-2 text-micro font-semibold text-white"
                  style={{ left: x(todayMs) + dw / 2 }}
                >
                  {t('schedule.gantt.today', { date: fmt.monthDay.format(todayMs) })}
                </div>
              )}
            </div>
          ))}

          <div className="relative">
            {/* 底：休息日、停工日、月界、今天 */}
            <div className="pointer-events-none absolute inset-0">
              {days.map((d) =>
                nonWork.has(dayKey(d)) ? (
                  <div
                    key={d}
                    className="absolute inset-y-0 bg-[repeating-linear-gradient(135deg,var(--rule-strong)_0_3px,transparent_3px_7px)]"
                    style={{ left: x(d), width: dw }}
                  />
                ) : isRest(d) && dw >= 6 ? (
                  <div key={d} className="absolute inset-y-0 bg-surface-2/60" style={{ left: x(d), width: dw }} />
                ) : null,
              )}
              {monthSegments(range)
                .slice(1)
                .map((m) => (
                  <div key={m.start} className="absolute inset-y-0 border-l border-rule" style={{ left: x(m.start) }} />
                ))}
              {showToday && (
                <div className="absolute inset-y-0 w-0.5 bg-danger-solid" style={{ left: x(todayMs) + dw / 2 - 1 }} />
              )}
            </div>

            {/* 项目整体 */}
            <div className={cn(rowClass, 'relative border-b border-rule bg-surface-2/40')}>
              <div
                className="absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-ink/80"
                style={{ left: x(projectStart), width: x(projectFinish + DAY_MS) - x(projectStart) }}
              />
            </div>

            {valid.map((task) => {
              const s = parseDateOnly(task.plannedStart)!;
              const f = parseDateOnly(task.plannedFinish)!;
              const left = x(s);
              const barWidth = Math.max(x(f + DAY_MS) - left, 4);
              // 一天的检查在按周看时画成菱形（里程碑的通行画法）：4px 宽的横条根本看不见
              const milestone = task.type === ScheduleTaskType.INSPECTION && s === f && scale === 'week' && !compact;
              const style: CSSProperties = milestone ? { left: left + barWidth / 2 - 6 } : { left, width: barWidth };
              if (colorMode === 'source' && task.color) style.backgroundColor = task.color;
              // 名称写在横条右边；太靠右放不下就写在左边
              const labelOnLeft = left + barWidth > width - 240;
              return (
                <div
                  key={task.key}
                  {...rowHover(task.key)}
                  className={cn(rowClass, 'relative border-b border-rule transition-colors last:border-0', rowTone(task.key))}
                >
                  <div
                    className={cn(
                      'absolute top-1/2',
                      milestone ? 'size-3 -translate-y-1/2 rotate-45 rounded-[2px]' : 'h-3.5 -translate-y-1/2 rounded-[4px]',
                      colorMode === 'source' && task.color ? '' : BAR_CLASS[task.type],
                    )}
                    style={style}
                    onMouseMove={canHover ? (e) => setTip({ task, x: e.clientX, y: e.clientY }) : undefined}
                    onMouseLeave={canHover ? () => setTip(null) : undefined}
                  />
                  {!compact && (
                    <span
                      className="pointer-events-none absolute top-1/2 -translate-y-1/2 whitespace-nowrap text-caption text-ink-2"
                      style={labelOnLeft ? { right: width - left + 8 } : { left: left + barWidth + 8 }}
                    >
                      {task.name}
                      <span className="ml-1.5 tabular-nums text-ink-4">{span(task)}</span>
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ---------------- 分隔线：盖在两块的交界上，不占宽度。命中区比看得见的线宽（手指上更宽） */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={t('schedule.gantt.resize')}
        aria-valuemin={0}
        aria-valuemax={leftWidth}
        aria-valuenow={leftSize}
        tabIndex={0}
        data-action="gantt.resize"
        className={cn(
          'group absolute inset-y-0 flex w-4 cursor-col-resize touch-none select-none flex-col justify-center focus-visible:outline-none pointer-coarse:w-7',
          // 收到 0 时整条命中区留在卡片里（骑在边上的话一半被裁掉，手指不好再拉出来）
          leftSize > 0 && '-translate-x-1/2',
        )}
        style={{ left: leftSize }}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          drag.current = { x: e.clientX, from: leftSize };
          setTip(null);
        }}
        onPointerMove={(e) => {
          if (drag.current) setLeftSize(drag.current.from + e.clientX - drag.current.x, false);
        }}
        onPointerUp={(e) => {
          if (!drag.current) return;
          const next = drag.current.from + e.clientX - drag.current.x;
          drag.current = null;
          // 拖到只剩一条缝就当收起：半截的名称列什么也看不清
          setLeftSize(next < 32 ? 0 : next);
        }}
        onPointerCancel={() => {
          drag.current = null;
        }}
        onDoubleClick={() => setLeftSize(leftSize < leftWidth ? leftWidth : 0)}
        onKeyDown={(e) => {
          const step = e.shiftKey ? 96 : 24;
          const next =
            e.key === 'ArrowLeft' ? leftSize - step
            : e.key === 'ArrowRight' ? leftSize + step
            : e.key === 'Home' ? 0
            : e.key === 'End' ? leftWidth
            : null;
          if (next === null) return;
          e.preventDefault();
          setLeftSize(next);
        }}
      >
        {/*
          一眼看得出能拖（客户原话「这条可以拖动的线，制作的明显一点」）：
          一直看得见的一条灰槽（不是一根细线），中间一个带两道竖纹的把手；悬停 / 拖动 / 键盘聚焦时整条变主色。
          把手 sticky 在屏幕中间：图有一屏多高，把手不会停在看不见的地方。不放图标（CR-081），只有形状
        */}
        <span className="absolute inset-y-0 left-1/2 w-1.5 -translate-x-1/2 border-x border-rule bg-surface-2 transition-colors group-hover:border-brand-500 group-hover:bg-brand-50 group-focus-visible:border-brand-500 group-focus-visible:bg-brand-50 group-active:border-brand-500 group-active:bg-brand-500 pointer-coarse:w-2" />
        <span className="sticky top-[40vh] bottom-[40vh] mx-auto flex h-10 w-3 items-center justify-center gap-0.5 rounded-full border border-rule-strong bg-surface shadow-xs transition-colors group-hover:border-brand-500 group-focus-visible:border-brand-500 group-active:border-brand-500 pointer-coarse:h-12 pointer-coarse:w-4">
          <span className="h-5 w-px bg-ink-3 group-hover:bg-brand-600 group-active:bg-brand-600" />
          <span className="h-5 w-px bg-ink-3 group-hover:bg-brand-600 group-active:bg-brand-600" />
        </span>
      </div>

      {tip && (
        <TipCard
          tip={tip}
          weekday={fmt.weekday}
          workdays={workdaysOf(tip.task)}
          calendarDays={scheduleTaskCalendarDays(tip.task)}
          nonWorkDays={scheduleTaskNonWorkOverlap(tip.task, nonWork)}
        />
      )}
    </div>
  );
}

/** 鼠标旁边的详情卡。跟着鼠标走、靠边时翻到另一侧；`pointer-events-none`，不挡鼠标 */
function TipCard({
  tip,
  weekday,
  workdays,
  calendarDays,
  nonWorkDays,
}: {
  tip: Tip;
  weekday: Intl.DateTimeFormat;
  workdays: number | null;
  calendarDays: number | null;
  nonWorkDays: number;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: tip.x + 14, top: tip.y + 16 });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const left = Math.min(tip.x + 14, window.innerWidth - w - 12);
    const top = tip.y + 16 + h > window.innerHeight ? tip.y - h - 12 : tip.y + 16;
    setPos({ left, top });
  }, [tip.x, tip.y]);

  const { task } = tip;
  const date = (value: string) => `${value} ${weekday.format(parseDateOnly(value)!)}`;
  return (
    <div
      ref={ref}
      role="tooltip"
      className="pointer-events-none fixed z-tooltip w-64 rounded-xl border border-rule-strong bg-popover p-3 text-caption shadow-pop"
      style={pos}
    >
      <p className="mb-2 text-body font-semibold text-ink">
        {task.code && <span className="mr-1.5 font-mono text-caption font-normal text-ink-4">{task.code}</span>}
        {task.name}
      </p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-ink-2">
        <dt className="text-ink-3">{t('schedule.gantt.col.type')}</dt>
        <dd>{t(`schedule.type.${task.type}`)}</dd>
        <dt className="text-ink-3">{t('schedule.gantt.col.start')}</dt>
        <dd className="tabular-nums">{date(task.plannedStart)}</dd>
        <dt className="text-ink-3">{t('schedule.gantt.col.finish')}</dt>
        <dd className="tabular-nums">{date(task.plannedFinish)}</dd>
        <dt className="text-ink-3">{t('schedule.gantt.duration')}</dt>
        <dd className="tabular-nums">
          {workdays !== null && `${t('schedule.gantt.workdays', { count: workdays })} · `}
          {t('schedule.gantt.calendarDays', { count: calendarDays ?? 0 })}
        </dd>
        {nonWorkDays > 0 && (
          <>
            <dt className="text-ink-3">{t('schedule.type.NONWORK')}</dt>
            <dd>{t('schedule.gantt.nonWorkIn', { count: nonWorkDays })}</dd>
          </>
        )}
        {task.sourceRef && task.sourceRef.length > 0 && (
          <>
            <dt className="text-ink-3">{t('schedule.gantt.col.source')}</dt>
            <dd>{task.sourceRef.map((r) => t('schedule.gantt.sourceRef', { page: r.page, row: r.weekRow })).join(' · ')}</dd>
          </>
        )}
      </dl>
    </div>
  );
}

function HeadCell({ width, right, children }: { width: number; right?: boolean; children: ReactNode }) {
  return (
    <div className={cn('shrink-0 truncate px-3 text-caption font-medium text-ink-3', right && 'text-right')} style={{ width }}>
      {children}
    </div>
  );
}

function BodyCell({ width, right, children }: { width: number; right?: boolean; children: ReactNode }) {
  return (
    <div className={cn('flex min-w-0 shrink-0 px-3', right && 'justify-end')} style={{ width }}>
      {children}
    </div>
  );
}

/**
 * 左表宽度（CR-084 可拖动分隔）：0 到它本来的宽度之间，按左表样式（`full` / `lite` / `compact`）分别记在本机。
 * 本机偏好而已，读写失败（隐私模式、清了站点数据）就当没记过，回到本来的宽度。
 * `set(v, false)` 只改不记 —— 拖的过程中每一帧都写 localStorage 没必要，松手再记。
 */
function useSplitWidth(columns: GanttColumns, natural: number): [number, (value: number, persist?: boolean) => void] {
  const key = `gantt.leftWidth.${columns}`;
  const [size, setSize] = useState(() => readSplitWidth(key, natural));
  // 换了左表样式（精简 ↔ 完整、手机）：换成那一种记下的宽度
  useEffect(() => setSize(readSplitWidth(key, natural)), [key, natural]);

  const set = (value: number, persist = true) => {
    const next = Math.round(Math.max(0, Math.min(natural, value)));
    setSize(next);
    if (!persist) return;
    try {
      window.localStorage.setItem(key, String(next));
    } catch {
      /* 记不住也不影响这一次 */
    }
  };
  return [size, set];
}

function readSplitWidth(key: string, natural: number): number {
  try {
    const saved = window.localStorage.getItem(key);
    if (saved !== null && /^\d+$/.test(saved)) return Math.min(Number(saved), natural);
  } catch {
    /* 读不到就用本来的宽度 */
  }
  return natural;
}

/** 元素的内容宽度，跟着窗口和侧栏变 */
function useElementWidth(ref: RefObject<HTMLElement | null>): number {
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

/** 真的有鼠标才做悬停：触屏上点一下也会冒出 mouseenter，卡片会卡在屏幕上 */
function useCanHover(): boolean {
  const [can] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine)').matches,
  );
  return can;
}
